import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Flex from '@react-css/flex';

import Card from 'storybookConfig/stories/cells/Card';
import Body from 'storybookConfig/stories/molecules/Body';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import SecondaryButton from 'storybookConfig/stories/molecules/Button/SecondaryButton';
import Icon from 'storybookConfig/stories/molecules/Icon';
import WashField from 'storybookConfig/stories/molecules/WashField';
import { ThemeBreakpoints } from 'storybookConfig/stories/theme';

const ICON_SIZE = 56;

export enum AnnouncementKinds {
  Action = 'action',
  Feature = 'feature',
}

type IconWrapperProps = Pick<AnnouncementProps, 'kind'>;

const IconWrapper = styled.div<IconWrapperProps>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  border-radius: 100%;

  position: absolute;
  z-index: 1;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);

  ${({ kind, theme }) => {
    switch (kind) {
      case AnnouncementKinds.Action:
        return css`
          background-color: ${theme.color.errorTint};
        `;
      case AnnouncementKinds.Feature:
        return css`
          background-color: ${theme.color.accentSubtle};
        `;
      default:
        return css``;
    }
  }}
`;

const Content = styled(Flex)`
  position: relative;
`;

const CloseIcon = styled(Icon)`
  position: absolute;
  z-index: 1;
  top: 6px;
  right: 6px;

  &:hover {
    cursor: pointer;
  }
`;

const Title = styled.strong`
  display: block;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  letter-spacing: 0.02em;
  font-family: ${({ theme }) => theme.font.primary};
`;

type WrapperProps = Pick<AnnouncementProps, 'kind'>;

const Wrapper = styled(Card)<WrapperProps>`
  position: relative;
  margin-top: ${ICON_SIZE / 2}px;

  /* The sidebar's width from md; the mobile menu sheet's below it. */
  width: 100%;

  @media screen and (${ThemeBreakpoints.md}) {
    width: 200px;
  }

  color: ${({ theme }) => theme.color.bodyTextOnDark};
  border-radius: 8px;
  padding: ${ICON_SIZE / 2 + 16}px 16px 16px;

  background: transparent;
  border: 0;
`;

interface AnnouncementProps {
  /**
   * The kind of announcement
   */
  kind: `${AnnouncementKinds}`;
  /**
   * The body of the announcement's message
   */
  children: React.ReactNode;
  /**
   * The text displayed inside the card's Button
   */
  ctaButtonText: string;
  /**
   * The event that the Button will trigger
   */
  onCtaButtonClick: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * The event that the Button will trigger
   */
  onCloseButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * The title that will appear on the new-feature cards
   */
  title: string;
  /**
   * Allows styling
   */
  className?: string;
}

const Announcement = ({
  kind,
  children,
  ctaButtonText,
  onCtaButtonClick,
  onCloseButtonClick,
  title,
  className,
}: AnnouncementProps) => {
  const shouldShowCloseButton = !!onCloseButtonClick;

  return (
    <Wrapper kind={kind} className={className} role="alert">
      {/* Action required on the inkwell wash, new features on dusk. */}
      <WashField
        wash={kind === AnnouncementKinds.Action ? 'inkwell' : 'dusk'}
        palette={kind === AnnouncementKinds.Action ? 'midnight' : 'dusk'}
      />

      {shouldShowCloseButton && (
        <CloseIcon
          as="button"
          onClick={onCloseButtonClick}
          size="20px"
          color="bodyTextOnDark"
          name="circle-x"
          aria-label="Dismiss announcement"
        />
      )}

      {kind === AnnouncementKinds.Action && (
        <>
          <IconWrapper kind={kind}>
            <Icon name="triangle-alert" size="30px" color="errorBase" />
          </IconWrapper>

          <Content column gap="16px">
            <Title>{title}</Title>

            <Body variant="Body/Body Small" align="center">
              {children}
            </Body>

            <Flex justifySpaceAround>
              <PrimaryButton kind="destructive" size="small" onClick={onCtaButtonClick}>
                {ctaButtonText}
              </PrimaryButton>
            </Flex>
          </Content>
        </>
      )}

      {kind === AnnouncementKinds.Feature && (
        <>
          <IconWrapper kind={kind}>
            <Icon name="megaphone" size="36px" color="accent" />
          </IconWrapper>

          <Content column gap="16px">
            <Title>{title}</Title>

            <Body variant="Body/Body Small" align="center">
              {children}
            </Body>

            <Flex justifySpaceAround>
              <SecondaryButton size="small" onClick={onCtaButtonClick}>
                {ctaButtonText}
              </SecondaryButton>
            </Flex>
          </Content>
        </>
      )}
    </Wrapper>
  );
};

export default Announcement;
